import { apiClient } from '@/core/api/client';
import {
  clearGuestSessionToken,
  getGuestSessionToken,
} from '@/features/guest-session/utils/guestSessionStorage';
import type { ChatMessage } from '../types/navigator-chat.types';

const CHAT_SESSION_STORAGE_KEY = 'chat_session_id';

export type RestoredConversation = {
  /** null → start with the greeting for the active mode. */
  messages: ChatMessage[] | null;
  isResumed: boolean;
};

/**
 * Mount-time conversation restore (extracted from useNavigatorChat).
 * `onSessionResolved` fires as soon as the saved id is known — before the
 * transcript fetch — matching the original ordering so an eager first send
 * still reuses the saved session.
 */
export const restoreNavigatorConversation = async (
  isAuthenticated: boolean,
  onSessionResolved: (sessionId: string | null) => void,
): Promise<RestoredConversation> => {
  try {
    if (sessionStorage.getItem('force_new_chat') === 'true') {
      sessionStorage.removeItem('force_new_chat');
      onSessionResolved(null);
      return { messages: null, isResumed: false };
    }
    // A guest session token in this tab means the saved chat session was
    // started anonymously (navigator_sessions.owner_user_id = NULL). An
    // authenticated user can't own that session, so reusing it makes the
    // JWT-scoped endpoints 403/404. Drop the guest artifacts and start a
    // fresh, owned session instead.
    if (isAuthenticated && getGuestSessionToken()) {
      clearGuestSessionToken();
      localStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
      onSessionResolved(null);
      return { messages: null, isResumed: false };
    }
    const savedSessionId = localStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    if (!savedSessionId || (!isAuthenticated && !getGuestSessionToken())) {
      // Every exit path reports the resolved session — including "nothing to
      // restore" — so a caller whose sessionId state is already non-null
      // (e.g. this effect re-running on an auth-state change) can't keep a
      // stale id past this restore.
      onSessionResolved(null);
      return { messages: null, isResumed: false };
    }
    onSessionResolved(savedSessionId);
    const { data } = await apiClient.get(`/chat-engine/conversation/${savedSessionId}`, {
      skipAuthRedirect: true,
    });
    if (data.messages?.length > 0) {
      return {
        messages: data.messages.map(
          (message: { id: string; role: string; content: string }) => ({
            id: message.id,
            role: message.role.toLowerCase() as 'user' | 'assistant',
            content: message.content,
          }),
        ),
        isResumed: true,
      };
    }
    return { messages: null, isResumed: false };
  } catch (error) {
    // 401 (stale guest token) / 403 (session owned by someone else) /
    // 404 (session gone) all mean the saved id is unusable — drop it so
    // the next turn starts a fresh, owned session instead of looping.
    const statusCode = (error as { statusCode?: number })?.statusCode;
    if (statusCode === 401 || statusCode === 403 || statusCode === 404) {
      localStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
      onSessionResolved(null);
    }
    return { messages: null, isResumed: false };
  }
};
